import { captureEvent } from "@Light/app/analytics";
import { useLight } from "@Light/services/light";
import type { AddressSearchResponse } from "@Light/services/lightTypes";
import { useForm, type UseFormRegisterReturn } from "react-hook-form";

type FormValues = { utility_account_number: string };

export type UseUtilityAccountNumberSearchOptions = {
  onFound: (address: AddressSearchResponse) => Promise<void> | void;
};

export type UseUtilityAccountNumberSearch = {
  field: UseFormRegisterReturn<"utility_account_number">;
  isLoading: boolean;
  error?: string;
  onSubmit: () => Promise<void>;
  reset: () => void;
};

/** Hook that powers `UtilityAccountNumberBottomSheet` */
export function useUtilityAccountNumberSearch({
  onFound,
}: UseUtilityAccountNumberSearchOptions): UseUtilityAccountNumberSearch {
  const { useSearchESIIDMutation } = useLight();
  const [searchESIID, { isLoading }] = useSearchESIIDMutation();
  const { handleSubmit, register, setError, reset, formState } =
    useForm<FormValues>();

  const onSubmit = handleSubmit(async ({ utility_account_number }) => {
    const esi_id = utility_account_number;
    try {
      const result = await searchESIID({ esi_id }).unwrap();
      captureEvent("enroll_esi_id_submitted", { esi_id, success: true });
      await onFound(result);
    } catch {
      captureEvent("enroll_esi_id_submitted", { esi_id, success: false });
      setError("utility_account_number", {
        message: "Could not find a location for that ESI ID.",
      });
    }
  });

  return {
    field: register("utility_account_number", { required: true }),
    isLoading,
    error: formState.errors.utility_account_number?.message,
    onSubmit,
    reset: () => {
      reset();
    },
  };
}
